import { Fragment, useState } from 'react'
import { Link, useParams } from 'react-router-dom'
import { useTranslation } from 'react-i18next'
import { apiPresStatusToUiStatus, formatFullName } from '../utils/participantMappers'
import { formatBytes } from '../utils/presentationValidation'
import { PresentationBadge } from '../components/PresentationStatusBadge'
import { PresentationActionModal } from '../components/PresentationActionModal'
import { PresentationUploadModal } from '../components/PresentationUploadModal'
import { PresentationRowActions } from '../components/AcceptedRowActions'
import { fromEventSummary } from './event/eventMapping'
import type { GetEventResponse } from './event/eventMapping'
import {
  useGetEventQuery,
  useListParticipantsQuery,
  useAcceptPresentationMutation,
  useRejectPresentationMutation,
  useAskForPresentationMutation,
  useAdminUploadPresentationMutation,
  type ParticipantSummary,
  type ListParticipantsResponse,
  type PresentationSummary,
} from '@proaxiv/server-api'

// ── Icons ─────────────────────────────────────────────────────────────────────

const BackArrow = () => (
  <svg
    width="14"
    height="14"
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="2"
    strokeLinecap="round"
    aria-hidden="true"
  >
    <path d="M19 12H5M12 5l-7 7 7 7" />
  </svg>
)

const ChevronIcon = ({ open }: { open: boolean }) => (
  <svg
    width="13"
    height="13"
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="2.5"
    strokeLinecap="round"
    aria-hidden="true"
    style={{ transform: open ? 'rotate(180deg)' : 'rotate(0deg)', transition: 'transform 0.15s' }}
  >
    <path d="M6 9l6 6 6-6" />
  </svg>
)

const FileIcon = () => (
  <svg
    width="13"
    height="13"
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="2"
    strokeLinecap="round"
    strokeLinejoin="round"
    aria-hidden="true"
    className="text-pax-gray shrink-0"
  >
    <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z" />
    <polyline points="14 2 14 8 20 8" />
  </svg>
)

// ── Helpers ───────────────────────────────────────────────────────────────────

/** File extension (uppercased, without the dot) inferred from the file name — the API doesn't
 *  return a separate content-type for presentation files, so this is the only "format" signal
 *  available to render in the archive table. */
function fileFormat(fileName: string | undefined): string {
  if (!fileName) return '—'
  const dot = fileName.lastIndexOf('.')
  if (dot === -1 || dot === fileName.length - 1) return '—'
  return fileName.slice(dot + 1).toUpperCase()
}

/** Any participant whose presentation has moved past "never asked" belongs in the archive:
 *  asked (reminder sent), uploaded (by the speaker or as an organizer backup), accepted, or
 *  rejected. Participants who were never asked have nothing to archive yet. */
function hasArchivablePresentation(reg: ParticipantSummary): boolean {
  const status = reg.presentation?.status
  return status != null && status !== 'NONE'
}

type PresActionKind = 'ask' | 'accept' | 'reject'

interface PresActionModal {
  kind: PresActionKind
  ids: string[]
}

interface PresUploadModal {
  participantId: string
  participantName: string
}

// ── Component ─────────────────────────────────────────────────────────────────

/**
 * Comprehensive, event-wide view of every presentation an organiser has requested,
 * received, approved, or rejected — across all participant statuses (unlike the
 * Accepted tab, which only shows presentations for currently-accepted participants).
 * Presentation actions (ask / upload backup / approve / reject / download) reuse the exact
 * same `PresentationRowActions` component as the Accepted tab, so organisers get one
 * consistent way to manage presentations (see #160). Scoped to `presentationOnly` here — this
 * page is purely about presentations, so the participant-management "Additional actions"
 * (participation reminder, ticket link) are intentionally not offered from it.
 */
export function PresentationsArchivePage() {
  const { t } = useTranslation()
  const { id } = useParams<{ id: string }>()

  const { data: getEventRaw, isLoading: loadingEvent } = useGetEventQuery(
    { eventId: id! },
    { skip: !id }
  )
  const event =
    getEventRaw != null ? fromEventSummary((getEventRaw as GetEventResponse).event) : undefined

  // Not truly paginated (see plan for issue #260) — this page loads every status then filters
  // client-side for an archivable presentation, so it just fetches the backend's capped maximum
  // page size rather than pretending to page through it.
  const { data, isLoading, isError } = useListParticipantsQuery(
    { eventId: id!, take: 50 },
    { skip: !id }
  )
  const allParticipants = (data as ListParticipantsResponse | undefined)?.participants ?? []
  const rows: ParticipantSummary[] = allParticipants
    .filter(hasArchivablePresentation)
    .sort((a, b) =>
      (b.presentation?.uploadedAt ?? b.presentation?.reminderSentAt ?? '').localeCompare(
        a.presentation?.uploadedAt ?? a.presentation?.reminderSentAt ?? ''
      )
    )

  const [askForPresentation] = useAskForPresentationMutation()
  const [acceptPresentation] = useAcceptPresentationMutation()
  const [rejectPresentation] = useRejectPresentationMutation()
  const [adminUploadPresentation] = useAdminUploadPresentationMutation()

  const [expandedKeys, setExpandedKeys] = useState<Set<string>>(new Set())
  const [presActionModal, setPresActionModal] = useState<PresActionModal | null>(null)
  const [presUploadModal, setPresUploadModal] = useState<PresUploadModal | null>(null)

  function toggleExpand(rowId: string) {
    setExpandedKeys((prev) => {
      const next = new Set(prev)
      if (next.has(rowId)) next.delete(rowId)
      else next.add(rowId)
      return next
    })
  }

  function openPresAction(kind: PresActionKind, ids: string[]) {
    setPresActionModal({ kind, ids })
  }

  async function handlePresActionConfirm({
    notify,
    message,
  }: {
    notify: boolean
    message?: string
  }) {
    if (!presActionModal || !id) return
    const { kind, ids } = presActionModal

    if (kind === 'ask') {
      await askForPresentation({
        participantId: ids[0],
        presentationRemindBody: { customMessage: message },
      })
    } else if (kind === 'accept') {
      await acceptPresentation({
        participantId: ids[0],
        presentationActionBody: { sendEmailNotification: notify },
      })
    } else if (kind === 'reject') {
      await rejectPresentation({
        participantId: ids[0],
        presentationRejectBody: { sendEmailNotification: notify, rejectionMessage: message },
      })
    }

    setPresActionModal(null)
  }

  async function handlePresentationUpload(participantId: string, file: File) {
    const formData = new FormData()
    formData.append('file', file)
    const result = await adminUploadPresentation({
      participantId,
      body: formData as unknown as { file: Blob },
    })
    if ('error' in result) throw result.error
  }

  function getPresActionModalConfig(modal: PresActionModal) {
    const { kind } = modal
    if (kind === 'ask')
      return {
        title: t('pres_ask_title_single'),
        confirmLabel: t('pres_ask_confirm'),
        showNotify: false,
        showMessage: true,
        messageLabel: t('pres_ask_message_label'),
        messagePlaceholder: t('pres_message_placeholder'),
      }
    if (kind === 'accept')
      return {
        title: t('pres_accept_title_single'),
        confirmLabel: t('pres_accept_confirm'),
        showNotify: true,
        showMessage: false,
      }
    return {
      title: t('pres_reject_title_single'),
      confirmLabel: t('pres_reject_confirm'),
      showNotify: true,
      showMessage: true,
      messageLabel: t('pres_message_label'),
      messagePlaceholder: t('pres_message_placeholder'),
    }
  }

  const loading = loadingEvent || isLoading

  return (
    <div className="mx-auto max-w-5xl space-y-6 px-4 py-6 md:px-6 md:py-8">
      <Link
        to={id ? `/event-planner/${id}` : '/event-planner'}
        className="text-pax-gray hover:text-pax-blue flex items-center gap-1.5 text-sm transition-colors focus-visible:outline-none"
      >
        <BackArrow />
        {event?.title ?? t('ep_title')}
      </Link>

      <div className="flex flex-col gap-1">
        <h1 className="font-display text-pax-black text-2xl font-bold tracking-tight md:text-3xl">
          {t('pres_archive_title')}
        </h1>
        <p className="text-pax-gray text-sm">{t('pres_archive_subtitle')}</p>
      </div>

      {loading && (
        <div className="border-pax-purple/20 animate-pulse rounded-2xl border bg-white p-12 text-center">
          <div className="bg-pax-purple/10 mx-auto h-4 w-32 rounded" />
        </div>
      )}

      {!loading && isError && (
        <div className="border-pax-red/20 bg-pax-red/5 rounded-2xl border p-6 text-center text-sm text-red-500">
          {t('pres_archive_error')}
        </div>
      )}

      {!loading && !isError && rows.length === 0 && (
        <p className="text-pax-gray px-4 py-10 text-center text-sm">{t('pres_archive_empty')}</p>
      )}

      {!loading && !isError && rows.length > 0 && (
        <>
          {/* Mobile card list */}
          <div data-testid="mobile-view" className="flex flex-col gap-3 md:hidden">
            {rows.map((row) => {
              const isExpanded = expandedKeys.has(row.id!)
              const presentation = row.presentation as PresentationSummary | undefined
              return (
                <div
                  key={row.id}
                  className="border-pax-purple/20 rounded-2xl border bg-white p-4 shadow-sm"
                >
                  <div className="flex items-start justify-between gap-3">
                    <div className="min-w-0 flex-1">
                      <p className="text-pax-black font-medium">
                        {formatFullName(row.firstName, row.lastName)}
                      </p>
                      <p className="text-pax-gray text-xs break-all">{row.email}</p>
                      <div className="mt-1.5">
                        <PresentationBadge
                          registrationId={row.id!}
                          meta={{
                            status: apiPresStatusToUiStatus(presentation?.status),
                            fileName: presentation?.fileName,
                            uploadedAt: presentation?.uploadedAt,
                            lastReminderAt: presentation?.reminderSentAt,
                            uploadedByOrganizer: presentation?.uploadedByOrganizer ?? false,
                          }}
                        />
                      </div>
                      {presentation?.fileName && (
                        <p className="text-pax-gray mt-1.5 flex items-center gap-1.5 text-xs">
                          <FileIcon />
                          <span className="truncate">{presentation.fileName}</span>
                          <span className="text-pax-gray/70">
                            · {fileFormat(presentation.fileName)}
                            {presentation.fileSize != null &&
                              ` · ${formatBytes(presentation.fileSize)}`}
                          </span>
                        </p>
                      )}
                      {presentation?.uploadedByOrganizer && (
                        <span className="bg-pax-purple/10 text-pax-purple mt-1.5 inline-flex rounded-full px-2 py-0.5 text-[10px] font-semibold">
                          {t('pres_archive_backup_upload')}
                        </span>
                      )}
                    </div>
                  </div>

                  {isExpanded && event && (
                    <div className="mt-3 border-t border-slate-100 pt-3">
                      <PresentationRowActions
                        reg={row}
                        event={event}
                        onPresAction={openPresAction}
                        onUpload={(pid, name) =>
                          setPresUploadModal({ participantId: pid, participantName: name })
                        }
                        presentationOnly
                      />
                    </div>
                  )}

                  <button
                    type="button"
                    onClick={() => toggleExpand(row.id!)}
                    aria-expanded={isExpanded}
                    className="text-pax-gray hover:text-pax-black mt-3 flex w-full items-center justify-center gap-1 rounded-lg border border-slate-100 py-1.5 text-xs font-medium transition-colors"
                  >
                    <ChevronIcon open={isExpanded} />
                    {t('reg_expand')}
                  </button>
                </div>
              )
            })}
          </div>

          {/* Desktop table */}
          <div data-testid="desktop-view" className="hidden md:block">
            <div className="border-pax-purple/30 overflow-hidden rounded-2xl border bg-white shadow-sm">
              <div className="overflow-x-auto">
                <table className="w-full border-collapse text-sm">
                  <thead>
                    <tr className="border-pax-purple/20 bg-pax-light-purple/60 border-b">
                      <th className="text-pax-gray px-4 py-3 text-left text-xs font-semibold tracking-wider">
                        {t('reg_col_name')}
                      </th>
                      <th className="text-pax-gray px-4 py-3 text-left text-xs font-semibold tracking-wider">
                        {t('acc_col_presentation')}
                      </th>
                      <th className="text-pax-gray px-4 py-3 text-left text-xs font-semibold tracking-wider">
                        {t('pres_archive_col_file')}
                      </th>
                      {/* Header left visually blank (#274); aria-label keeps a real accessible name. */}
                      <th
                        aria-label={t('reg_col_actions')}
                        className="text-pax-gray px-4 py-3 text-right text-xs font-semibold tracking-wider"
                      ></th>
                    </tr>
                  </thead>
                  <tbody>
                    {rows.map((row) => {
                      const isExpanded = expandedKeys.has(row.id!)
                      const presentation = row.presentation as PresentationSummary | undefined
                      return (
                        <Fragment key={row.id}>
                          <tr className="border-pax-purple/10 hover:bg-pax-light-purple/40 border-b transition-colors last:border-0">
                            <td className="text-pax-black px-4 py-3 font-medium">
                              {formatFullName(row.firstName, row.lastName)}
                              <p className="text-pax-gray text-xs font-normal">{row.email}</p>
                            </td>
                            <td className="px-4 py-3">
                              <PresentationBadge
                                registrationId={row.id!}
                                meta={{
                                  status: apiPresStatusToUiStatus(presentation?.status),
                                  fileName: presentation?.fileName,
                                  uploadedAt: presentation?.uploadedAt,
                                  lastReminderAt: presentation?.reminderSentAt,
                                  uploadedByOrganizer: presentation?.uploadedByOrganizer ?? false,
                                }}
                              />
                            </td>
                            <td className="text-pax-black px-4 py-3 text-xs">
                              {presentation?.fileName ? (
                                <div className="flex items-center gap-1.5">
                                  <FileIcon />
                                  <span className="max-w-[16rem] truncate">
                                    {presentation.fileName}
                                  </span>
                                  <span className="text-pax-gray whitespace-nowrap">
                                    · {fileFormat(presentation.fileName)}
                                    {presentation.fileSize != null &&
                                      ` · ${formatBytes(presentation.fileSize)}`}
                                  </span>
                                  {presentation.uploadedByOrganizer && (
                                    <span className="bg-pax-purple/10 text-pax-purple ml-1 inline-flex rounded-full px-2 py-0.5 text-[10px] font-semibold whitespace-nowrap">
                                      {t('pres_archive_backup_upload')}
                                    </span>
                                  )}
                                </div>
                              ) : (
                                <span className="text-pax-gray">—</span>
                              )}
                            </td>
                            <td className="px-2 py-2">
                              <div className="flex justify-end">
                                <button
                                  type="button"
                                  onClick={() => toggleExpand(row.id!)}
                                  aria-expanded={isExpanded}
                                  className="text-pax-gray hover:text-pax-black flex items-center gap-1 rounded px-2 py-1 text-xs transition-colors"
                                >
                                  <ChevronIcon open={isExpanded} />
                                  {t('reg_expand')}
                                </button>
                              </div>
                            </td>
                          </tr>
                          {isExpanded && event && (
                            <tr className="bg-pax-light-purple/20">
                              <td colSpan={4} className="px-6 py-4">
                                <PresentationRowActions
                                  reg={row}
                                  event={event}
                                  onPresAction={openPresAction}
                                  onUpload={(pid, name) =>
                                    setPresUploadModal({
                                      participantId: pid,
                                      participantName: name,
                                    })
                                  }
                                  presentationOnly
                                />
                              </td>
                            </tr>
                          )}
                        </Fragment>
                      )
                    })}
                  </tbody>
                </table>
              </div>
            </div>
          </div>
        </>
      )}

      {presActionModal !== null &&
        (() => {
          const cfg = getPresActionModalConfig(presActionModal)
          return (
            <PresentationActionModal
              {...cfg}
              onConfirm={(r) => void handlePresActionConfirm(r)}
              onCancel={() => setPresActionModal(null)}
            />
          )
        })()}

      {presUploadModal !== null && (
        <PresentationUploadModal
          registrationId={presUploadModal.participantId}
          participantName={presUploadModal.participantName}
          onUpload={handlePresentationUpload}
          onDone={() => setPresUploadModal(null)}
          onCancel={() => setPresUploadModal(null)}
        />
      )}
    </div>
  )
}
